
import { RevContainer } from '@ds/components/Container'
import isEmpty from 'lodash/isEmpty'

import Breadcrumb from '@app/components/Breadcrumb'
import Author from '@cms/components/Author'
import BlockRenderer from '@cms/components/BlockRenderer.vue'
import BuyingGuides from '@cms/components/BuyingGuides'
import Heading from '@cms/components/Heading'
import HeroBanner from '@cms/components/HeroBanner'
import { Container } from '@cms/components/private/Container'
import {
  DESCRIPTION_TAGS,
  IMAGE_TAGS,
  TITLE_TAGS,
  createMetaForGroupedTags,
} from '@cms/helpers/meta'
import { valueBuilder as zoneBuilder } from '@cms/helpers/tracking'
import { generateAlternateLinks } from '@core/helpers/head'
import { tw } from '@core/tailwind'
import logger from '@logger'
import { LinkType } from '@navigation/constant'
import { ROUTES } from '@router'
import { trackClick } from '@tracking/events'

import translations from './Article.translations'
import {
  BLOCK_WIDTH,
  FULL_WIDTH_BLOCKS,
  checkCurrentCategory,
  getArticle,
  getBlockWidth,
} from './helpers'

export default {
  components: {
    Author,
    BlockRenderer,
    Breadcrumb,
    BuyingGuides,
    Container,
    Heading,
    HeroBanner,
    RevContainer,
  },

  async asyncData({ error, redirect, route, store }) {
    try {
      const article = await getArticle(store, route)

      const { isCurrentCategoryCorrect, correctCategory } =
        checkCurrentCategory({
          categories: article.categories,
          currentRouteCategory: route.params.category,
        })

      if (!isCurrentCategoryCorrect) {
        return redirect(301, {
          ...route,
          params: {
            pageName: route.params.pageName,
            category: correctCategory,
          },
        })
      }

      const breadcrumb = {
        currentPageTitle: article.titleBreadcrumb,
        intermediaryPath: (article.categories || []).map(
          ({ title: parentTitle, slug }) => ({
            title: parentTitle,
            slug: {
              type: LinkType.Internal,
              name:
                slug === 'content'
                  ? ROUTES.BUYING_GUIDE.ROOT_HUB
                  : ROUTES.BUYING_GUIDE.HUB,
              params: {
                unifiedName: slug,
              },
            },
          }),
        ),
      }

      const headAlternateLinks = await generateAlternateLinks({
        link: route,
        baseUrl: store.getters['config/baseURL'],
        countries: article.meta.alternateLinks.map(({ country }) => country),
      })

      return {
        ...article,
        author: article.author.props,
        relatedArticles: !isEmpty(article.relatedArticles)
          ? {
              ...article.relatedArticles.props,
              id: article.relatedArticles.id,
            }
          : {},
        breadcrumb,
        headTags: {
          meta: article.meta,
          title: article.titleSeo,
          alternateLinks: headAlternateLinks,
        },
      }
    } catch (err) {
      logger.error('Error fetching article content', { error: err })

      const statusCode = err.status_code || 404
      const message = err.message || ''

      return error({
        statusCode,
        message,
      })
    }
  },

  data() {
    return {
      author: {},
      badge: '',
      banner: {},
      blocks: [],
      breadcrumb: {},
      headTags: {
        meta: {},
        title: '',
        alternateLinks: [],
      },
      relatedArticles: {},
      summary: '',
      title: '',
    }
  },

  head() {
    return {
      title: this.headTags.title,
      titleTemplate: `%s | ${this.$t(translations.companyName)}`,
      meta: createMetaForGroupedTags([
        {
          tags: DESCRIPTION_TAGS,
          content: this.headTags.meta.description,
        },
        {
          tags: TITLE_TAGS,
          content: this.headTags.meta.title,
        },
        {
          tags: IMAGE_TAGS,
          content: this.headTags.meta.image?.src,
        },
      ]),
      link: [
        ...this.headTags.alternateLinks,
        {
          rel: 'canonical',
          href: `${this.$store.getters['config/baseURL']}${this.$route.path}`,
        },
      ],
    }
  },

  computed: {
    hasImageBanner() {
      return !isEmpty(this.banner)
    },
    hasAuthor() {
      return !isEmpty(this.author)
    },
    hasRelatedArticles() {
      return (
        !isEmpty(this.relatedArticles) && !this.$config.KILL_RELATED_ARTICLES
      )
    },
    hasBlocks() {
      return !isEmpty(this.blocks)
    },
    hasEndingBlocks() {
      return [this.author, this.relatedArticles].some(
        (block) => !isEmpty(block),
      )
    },
  },

  methods: {
    containerClasses(blockType) {
      const blockWidth = getBlockWidth(blockType)

      const spacingClasses = tw`px-8 md:px-0 md:mx-auto`

      const classesByBlockWidth = {
        [BLOCK_WIDTH.NARROW_WIDTH]: tw`${spacingClasses} md:max-w-6xl`,
        [BLOCK_WIDTH.MEDIUM_WIDTH]: spacingClasses,
        [BLOCK_WIDTH.FULL_WIDTH]: '',
      }

      return classesByBlockWidth[blockWidth]
    },

    getBlockPosition(index) {
      return index + 1
    },

    isFullWidth(blockType) {
      return FULL_WIDTH_BLOCKS.includes(blockType)
    },

    sendTrackingData({ type, title }) {
      const { name, params } = this.$route

      trackClick({
        name: title,
        value: type,
        zone: zoneBuilder([name, params.pageName]),
      })
    },
  },
}
